/**
 * Sales fill (Updated Forecast toggle): periods before a SKU's first sale
 * filled with an estimate borrowed from the product's other SKUs, or its
 * category when the product is new too (control-tower
 * forecast/sales-fill.js). The run carries the result in
 * `forecast_model.sales_fill`, and the filled history rows are marked
 * `filled: true` + `fill_source`.
 *
 * Everything here is how the page (and the SKU report) say it, one point each.
 */

const UNIT_WORDS = { month: ["month", "months"], week: ["week", "weeks"], day: ["day", "days"] };

const periodLabel = (iso, unit) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return unit === "month" || !unit
    ? d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" })
    : d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
};
const range = (from, to, unit) => {
  const a = periodLabel(from, unit);
  const b = periodLabel(to, unit);
  return a === b ? a : `${a} – ${b}`;
};
const pct = (v) => Math.round((Number(v) || 0) * 1000) / 10;

/** Where a source's sales came from, in words. */
export const fillSourceText = (s) =>
  s.source === "category"
    ? `${s.product_count ? `${s.product_count} other products` : "other products"} in the ${s.label ? `"${s.label}"` : "same"} category`
    : `${s.sku_count || "the"} other SKU${s.sku_count === 1 ? "" : "s"} of ${s.label ? `"${s.label}"` : "the same product"}`;

/** Is a history row a filled (estimated) one? */
export const isFilledRow = (row) => Boolean(row?.filled);

/**
 * The points to show for a run's sales fill, or [] when it wasn't asked for.
 * → { applied, title, points: [string] }
 */
export const salesFillPoints = (fill) => {
  if (!fill?.requested) return { applied: false, title: null, points: [] };
  if (!fill.applied) {
    return {
      applied: false,
      title: "Sales fill was on, but nothing was filled",
      points: [fill.message || "There was nothing to borrow from, so the SKU's own sales were used as recorded."],
    };
  }
  const [one, many] = UNIT_WORDS[fill.unit] || ["period", "periods"];
  const n = fill.periods;
  return {
    applied: true,
    title: "Sales filled (estimated)",
    points: [
      fill.first_sale
        ? `${n} ${n === 1 ? one : many} before the first sale (${range(fill.from, fill.to, fill.unit)}) had no sales because the SKU wasn't selling yet, so they were estimated.`
        : `This SKU has no sales yet (a new launch), so all ${n} ${n === 1 ? one : many} of its history (${range(fill.from, fill.to, fill.unit)}) were estimated.`,
      ...(fill.sources || []).map((s) =>
        s.scale_basis === "average_sku"
          ? `${range(s.from, s.to, fill.unit)}: borrowed from ${fillSourceText(s)}, as an average SKU of them (${pct(s.scale)}% of their sales) — this SKU has too little sales of its own to compare.`
          : `${range(s.from, s.to, fill.unit)}: borrowed from ${fillSourceText(s)}, scaled to ${pct(s.scale)}% of their sales (this SKU's level).`,
      ),
      `These are estimates, not real sales: about ${Math.round(Number(fill.units) || 0).toLocaleString("en-US")} units in total, shown in grey on the chart.`,
      fill.tested_on_estimates
        ? "This SKU has so few real sales that the forecast was partly tested on these estimates, so treat the forecast and its accuracy as a rough guide. The inventory ledger shows real sales only."
        : "They let the forecast see a full year's pattern. It was still tested on real sales only, and the inventory ledger shows real sales only.",
    ],
  };
};
